// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useWidgetBadges from "@insite/client-framework/Common/Hooks/useWidgetBadges";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import {
    BadgeImagePlacementValues,
    BadgeModel,
    BadgeStyleValues,
    BadgeTypeValues,
} from "@insite/client-framework/Types/ApiModels";
import QtyBreakPricing from "@sc/XDL/Icons/QtyBreakPricing";
import { parseBoolean } from "@sc/XDL/Utilities";

interface Props {
    productContext: ProductContextModel;
    showTextBadges?: boolean;
    showImageBadges?: boolean;
    showPreviouslyPurchased?: boolean;
    showQtyBreakPricing?: boolean;
}

export interface TagsType {
    previouslyPurchased: boolean;
}

export const previouslyPurchasedTagId = "previouslyPurchased";
export const qtyBreakPricingId = "qtyBreakPricing";

const defaultTag: BadgeModel = {
    badgeColorHexCode: "FFD0B0",
    badgeType: BadgeTypeValues.Text,
    badgeStyle: BadgeStyleValues.Rectangle,
    detailWidgetBadgeSize: "",
    displayOnBadgeWidget: true,
    displayOnProductImages: true,
    displayText: "",
    id: "",
    imageAltText: "",
    largeImageBadgePath: "",
    largeImagePlacement: BadgeImagePlacementValues.None,
    largeImageTextSize: "",
    name: "",
    tagName: "",
    otherImageBadgePath: "",
    otherImagePlacement: BadgeImagePlacementValues.None,
    otherImageTextSize: "",
    sortOrder: -1,
    textColorHexCode: "",
};

const sortBadges = (badges: BadgeModel[] | null) => {
    return badges?.sort(badge => badge.sortOrder);
};

const xUseWidgetBadges = ({
    productContext,
    showTextBadges,
    showImageBadges,
    showPreviouslyPurchased = true,
    showQtyBreakPricing = true,
}: Props) => {
    const { textBadges, imageBadges } = useWidgetBadges({ productContext, showImageBadges, showTextBadges });

    let imageBadgesAndTags = sortBadges(imageBadges);
    let textBadgesAndTags = sortBadges(textBadges);

    const previouslyPurchased = parseBoolean(productContext.product.properties?.isPreviouslyPurchased);
    // Previously purchased has the highest priority
    if (previouslyPurchased && showPreviouslyPurchased) {
        const prevPurchasedTag: BadgeModel = {
            ...defaultTag,
            displayText: translate("Prev. Purchase"),
            id: previouslyPurchasedTagId,
            name: translate("Previously Purchased"),
            tagName: translate("Previously Purchased"),
        };
        // add it at the first
        if (!textBadgesAndTags) {
            textBadgesAndTags = [];
        }
        textBadgesAndTags?.unshift(prevPurchasedTag);
    }
    const isQtyBreakPricing = parseBoolean(
        productContext?.productInfo?.pricing?.additionalResults?.qtyBreak || productContext.product.properties.qtyBreak,
    );

    if (isQtyBreakPricing && showQtyBreakPricing) {
        const qtyBreakPricingMessage = translate("Qty Break Pricing");
        const qtyBreakPricingTag: BadgeModel = {
            ...defaultTag,
            badgeType: BadgeTypeValues.Image,
            id: qtyBreakPricingId,
            imageAltText: qtyBreakPricingMessage,
            largeImageBadgePath: QtyBreakPricing as any,
            name: qtyBreakPricingMessage,
            tagName: qtyBreakPricingMessage,
        };

        // add it at the first
        if (!imageBadgesAndTags) {
            imageBadgesAndTags = [];
        }
        imageBadgesAndTags?.unshift(qtyBreakPricingTag);
    }

    return {
        textBadges: showTextBadges ? textBadgesAndTags : [],
        imageBadges: showImageBadges ? imageBadgesAndTags : [],
    };
};

export default xUseWidgetBadges;
